import { ENDPOINTS } from "../endpoints";
import {
  CreateOrganizationDto,
  OrganizationDto,
  UpdateOrganizationDto,
  UserDto,
} from "../types/dtos";
import { CustomResponseEntity, PaginatedResponse, PaginationParams } from "../types/common";
import { api } from "../client/axiosClient";

const NO_ORG_CONTEXT = { headers: { "X-Organization-Id": "" } };

export interface AdminOrganizationSearchParams extends PaginationParams {
  search?: string;
}

export class AdminOrganizationService {
  async findAllForAdmin(
    params?: PaginationParams
  ): Promise<CustomResponseEntity<OrganizationDto[]>> {
    const url = ENDPOINTS.organizations.adminAll();
    return api.get(url, { params, ...NO_ORG_CONTEXT });
  }

  async findAllForAdminPaginated(
    params?: AdminOrganizationSearchParams
  ): Promise<CustomResponseEntity<PaginatedResponse<OrganizationDto>>> {
    const url = ENDPOINTS.organizations.adminAll();
    return api.get(url, { params, ...NO_ORG_CONTEXT });
  }

  async findById(id: string): Promise<CustomResponseEntity<OrganizationDto>> {
    return api.get(`${ENDPOINTS.organizations.base()}/${id}`, NO_ORG_CONTEXT);
  }

  async findDetailsById(id: string): Promise<CustomResponseEntity<OrganizationDto>> {
    return api.get(ENDPOINTS.organizations.details(id), NO_ORG_CONTEXT);
  }

  async create(
    data: CreateOrganizationDto,
    logoFile?: File | null
  ): Promise<CustomResponseEntity<OrganizationDto>> {
    const response = await api.post<CustomResponseEntity<OrganizationDto>>(
      ENDPOINTS.organizations.adminCreate(),
      data,
      NO_ORG_CONTEXT
    );
    if (logoFile && response.data) {
      const orgId = (response.data as OrganizationDto).id;
      await this.uploadLogo(orgId, logoFile);
    }
    return response;
  }

  async update(
    id: string,
    data: UpdateOrganizationDto,
    logoFile?: File | null
  ): Promise<CustomResponseEntity<OrganizationDto>> {
    const response = await api.put<CustomResponseEntity<OrganizationDto>>(
      ENDPOINTS.organizations.adminUpdate(id),
      data,
      NO_ORG_CONTEXT
    );
    if (logoFile) {
      await this.uploadLogo(id, logoFile);
    }
    return response;
  }

  async uploadLogo(
    id: string,
    logoFile: File
  ): Promise<CustomResponseEntity<OrganizationDto>> {
    const formData = new FormData();
    formData.append("organization", JSON.stringify({}));
    formData.append("logo", logoFile);
    return api.put(`${ENDPOINTS.organizations.base()}/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Organization-Id": id,
      },
    });
  }

  async delete(id: string): Promise<CustomResponseEntity<void>> {
    return api.delete(ENDPOINTS.organizations.adminDelete(id), NO_ORG_CONTEXT);
  }
}

export class AdminUserService {
  async findAllForAdmin(params?: PaginationParams): Promise<CustomResponseEntity<UserDto[]>> {
    return api.get(ENDPOINTS.users.base(), { params, ...NO_ORG_CONTEXT });
  }

  async findById(id: string): Promise<CustomResponseEntity<UserDto>> {
    return api.get(`${ENDPOINTS.users.base()}/${id}`, NO_ORG_CONTEXT);
  }

  async create(data: UserDto): Promise<CustomResponseEntity<UserDto>> {
    return api.post(ENDPOINTS.users.create(), data, NO_ORG_CONTEXT);
  }

  async update(id: string, data: UserDto): Promise<CustomResponseEntity<UserDto>> {
    return api.put(ENDPOINTS.users.update(id), data, NO_ORG_CONTEXT);
  }

  async delete(id: string): Promise<CustomResponseEntity<void>> {
    return api.delete(`${ENDPOINTS.users.base()}/${id}`, NO_ORG_CONTEXT);
  }
}

export const adminOrganizationService = new AdminOrganizationService();
export const adminUserService = new AdminUserService();
